import type { HomeCardId } from "@wato/business-types";
import type { MessageKey } from "@wato/i18n";
import type { HomeKpiView } from "../../api/analytics-types";

/** Where a home card links (relative to /w/:id) and which KPI value (if any) it can show. */
export interface HomeCardDef {
  readonly to: string;
  readonly title: MessageKey;
  /** Reads the card's number from the KPIs; undefined = no source yet, the card shows its empty state. */
  readonly value?: (kpis: readonly HomeKpiView[]) => HomeCardValue | undefined;
}
type HomeCardMain =
  | { readonly kind: "count"; readonly n: number }
  | {
      readonly kind: "money";
      readonly money: readonly { currency: string; amountMinor: string }[];
    };
/** A secondary line under the number; `text` params are formatted by the card (money, %, duration). */
export type HomeCardDetail =
  | { readonly key: MessageKey; readonly kind: "count"; readonly n: number; readonly to?: string }
  | { readonly key: MessageKey; readonly kind: "money"; readonly money: readonly MoneyView[] }
  | { readonly key: MessageKey; readonly kind: "percent"; readonly basisPoints: number }
  | { readonly key: MessageKey; readonly kind: "duration"; readonly seconds: number };
type MoneyView = { currency: string; amountMinor: string };
interface HomeCardValue {
  readonly main?: HomeCardMain;
  readonly details: readonly HomeCardDetail[];
}

type Kpis = readonly HomeKpiView[];
const num = (kpis: Kpis, id: string, key: string): number | undefined =>
  kpis.find((k) => k.id === id)?.counts?.[key];
const moneyOf = (kpis: Kpis, id: string): readonly MoneyView[] | undefined => {
  const m = kpis.find((k) => k.id === id)?.money;
  return m && m.length > 0 ? m : undefined;
};
const bps = (kpis: Kpis, id: string): number | undefined =>
  kpis.find((k) => k.id === id)?.basisPoints ?? undefined;
const dk = (k: string) => `dashboard.home.profile.detail.${k}` as MessageKey;

const compose = (
  main: HomeCardMain | undefined,
  details: readonly (HomeCardDetail | undefined)[],
): HomeCardValue | undefined => {
  const d = details.filter((x): x is HomeCardDetail => x !== undefined);
  return main || d.length > 0 ? { ...(main ? { main } : {}), details: d } : undefined;
};
const cnt = (n: number | undefined): HomeCardMain | undefined =>
  n === undefined ? undefined : { kind: "count", n };
const count = (id: string, key: string) => (kpis: Kpis) => compose(cnt(num(kpis, id, key)), []);
const money = (id: string) => (kpis: Kpis) => {
  const m = moneyOf(kpis, id);
  return compose(m ? { kind: "money", money: m } : undefined, []);
};
const countDetail = (
  key: string,
  n: number | undefined,
  to?: string,
): HomeCardDetail | undefined =>
  n === undefined ? undefined : { key: dk(key), kind: "count", n, ...(to ? { to } : {}) };
const title = (id: HomeCardId) => `dashboard.home.profile.card.${id}` as MessageKey;

/** B54: the inbox URL filter for "waiting for a reply > 1 h" (open conversations). */
export const UNANSWERED_INBOX_LINK = "conversations?status=open&unansweredOver=60";

export const HOME_CARDS: Readonly<Record<HomeCardId, HomeCardDef>> = {
  unread_chats: {
    to: "conversations",
    title: title("unread_chats"),
    value: (k) => {
      const open = num(k, "conversations", "open") ?? num(k, "response_time", "openConversations");
      const median = num(k, "response_time", "medianFirstResponseSeconds");
      return compose(cnt(open), [
        countDetail(
          "unanswered",
          num(k, "response_time", "unansweredOver1h"),
          UNANSWERED_INBOX_LINK,
        ),
        median === undefined
          ? undefined
          : { key: dk("firstResponse"), kind: "duration", seconds: median },
      ]);
    },
  },
  ai_handoffs: {
    to: "conversations",
    title: title("ai_handoffs"),
    value: (k) =>
      compose(cnt(num(k, "ai_handoffs", "last24h")), [
        countDetail("ungroundedPrice", num(k, "ai_handoffs", "ungroundedPrice") || undefined),
      ]),
  },
  orders_today: {
    to: "store/orders",
    title: title("orders_today"),
    value: (k) => {
      const m = moneyOf(k, "orders");
      return compose(cnt(num(k, "orders", "ordersToday")), [
        m ? { key: dk("revenue"), kind: "money", money: m } : undefined,
      ]);
    },
  },
  abandoned_carts: {
    to: "carts",
    title: title("abandoned_carts"),
    value: count("orders", "openCarts"),
  },
  quotes_waiting: {
    to: "sell",
    title: title("quotes_waiting"),
    value: (k) =>
      compose(cnt(num(k, "quotes_accepted", "waiting")), [
        countDetail("sentQuotes", num(k, "quotes_accepted", "sent30d")),
        countDetail("accepted", num(k, "quotes_accepted", "accepted30d")),
      ]),
  },
  balances_due: {
    to: "payments",
    title: title("balances_due"),
    value: money("outstanding_balance"),
  },
  payments_today: {
    to: "payments",
    title: title("payments_today"),
    value: (k) => {
      if (!k.some((x) => x.id === "payments_today")) return undefined;
      // A real zero (nothing received yet today) is shown as 0, not as a missing source.
      return money("payments_today")(k) ?? compose({ kind: "count", n: 0 }, []);
    },
  },
  bookings_today: {
    to: "bookings",
    title: title("bookings_today"),
    value: count("bookings", "today"),
  },
  tables_tonight: {
    to: "bookings",
    title: title("tables_tonight"),
    value: count("tables_tonight", "tonight"),
  },
  no_shows: {
    to: "bookings",
    title: title("no_shows"),
    value: (k) => {
      const rate = bps(k, "no_show_rate");
      return compose(cnt(num(k, "bookings", "noShows7d")), [
        rate === undefined
          ? undefined
          : { key: dk("noShowRate"), kind: "percent", basisPoints: rate },
      ]);
    },
  },
  trips_this_month: {
    to: "trips/packages",
    title: title("trips_this_month"),
    value: count("trips_this_month", "tripsThisMonth"),
  },
  deposits_waiting: {
    to: "trips/bookings",
    title: title("deposits_waiting"),
    value: count("deposits_collected", "depositsDue"),
  },
  documents_missing: {
    to: "trips/bookings",
    title: title("documents_missing"),
    value: count("deposits_collected", "awaitingDocuments"),
  },
  new_leads: { to: "pipeline", title: title("new_leads"), value: count("leads", "newLeads7d") },
  viewings_this_week: {
    to: "bookings",
    title: title("viewings_this_week"),
    value: count("viewings", "thisWeek"),
  },
  deals_open: { to: "pipeline", title: title("deals_open"), value: count("deals_open", "open") },
};
